import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import DynamicInfoCardHighlight from 'component/DynamicInfoCardHighlight'
import useClientTracking from 'shared/hooks/useClientTracking'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { SafeHtmlContent } from 'site/shared/components/SafeHtmlContent'
import { StyledContainer } from './DynamicInfoCardHighlight.style'
import {
  CarouselItem,
  DynamicInfoCardHighlightContainerProps,
  NavigationPair,
  NavigationSliderCommand
} from './DynamicInfoCardHighlightContainer.types'
import mod from './utils/mod'

const DynamicInfoCardHighlightContainer = ({
  props
}: DynamicInfoCardHighlightContainerProps) => {
  const {
    sliderObjects: carouselItems = [],
    param1,
    param2,
    styleMap,
    showNavigationSliders,
    trackingObjectclassUuid
  } = props

  const styleProps = useStyleMaps(styleMap)

  const [searchParams, setSearchParams] = useSearchParams()

  const itemsMap = useMemo(() => {
    return carouselItems.reduce(
      (acc, item) => {
        acc[item.uuid] = item
        return acc
      },
      {} as Record<string, CarouselItem>
    )
  }, [carouselItems])

  const groupEntitiesMap = useMemo(() => {
    const groupMenu: Record<string, NavigationPair[]> = {}

    carouselItems.forEach((item) => {
      const [firstMenu, secondMenu] = item.navigationMenu
      const pair = {
        leftMenu: firstMenu,
        rightMenu: secondMenu,
        pairUuid: item.uuid
      }

      if (firstMenu?.des) {
        if (!groupMenu[firstMenu?.des]) {
          groupMenu[firstMenu.des] = [pair]
        } else {
          groupMenu[firstMenu?.des].push(pair)
        }
      }
    })

    return groupMenu
  }, [carouselItems])

  const updateUrl = useCallback(
    (nextCarouselItem: CarouselItem) => {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams)
          const menu = nextCarouselItem.navigationMenu

          if (menu?.[0]?.des) {
            newParams.set(param1, menu[0].des)
          }

          if (param2 && menu?.[1]?.des) {
            newParams.set(param2, menu[1].des)
          }

          return newParams
        },
        { replace: true }
      )
    },
    [param1, param2, setSearchParams]
  )

  const groupKeys = useMemo(
    () => Object.keys(groupEntitiesMap),
    [groupEntitiesMap]
  )

  const firstItem = carouselItems[0]

  const param1Value =
    searchParams.get(param1) || firstItem?.navigationMenu?.[0]?.des || ''

  const param2Value = param2
    ? searchParams.get(param2) || firstItem?.navigationMenu?.[1]?.des || ''
    : undefined

  useEffect(() => {
    const isParam1Valid =
      !param1 || (param1Value && groupEntitiesMap[param1Value])
    const isParam2Valid =
      !param2 ||
      (param2Value &&
        groupEntitiesMap[param1Value]?.some(
          (pair) => pair.rightMenu.des === param2Value
        ))

    const masMissingParam1 = !searchParams.get(param1)
    const hasMissingParam2 = param2 && !searchParams.get(param2)

    const hasMissingParams = masMissingParam1 || hasMissingParam2

    if ((!isParam1Valid || !isParam2Valid || hasMissingParams) && firstItem) {
      updateUrl(firstItem)
    }
  }, [
    param1Value,
    param2Value,
    groupEntitiesMap,
    firstItem,
    param1,
    param2,
    updateUrl,
    searchParams
  ])

  const current = useMemo(() => {
    if (!param1Value) return carouselItems[0]

    if (param2Value) {
      const item = groupEntitiesMap[param1Value]?.find(
        (pair) => pair.rightMenu.des === param2Value
      )
      if (item) return itemsMap[item.pairUuid]
    }

    const fallbackUuid = groupEntitiesMap[param1Value]?.[0]?.pairUuid
    return fallbackUuid ? itemsMap[fallbackUuid] : undefined
  }, [carouselItems, groupEntitiesMap, param1Value, param2Value, itemsMap])

  useClientTracking({
    trackingObjectclassUuid,
    objectUuid: current?.trackingObjectUuid
  })

  const getNextItem = useCallback(
    (direction: number, isMain: boolean) => {
      const fallback = carouselItems[0]

      if (!param1Value) {
        if (direction === 1) {
          return carouselItems[1] || fallback
        }
        return carouselItems[carouselItems.length - 1] || fallback
      }

      if (isMain) {
        const index = groupKeys.findIndex((item) => item === param1Value)
        const nextPos = mod(index + direction, groupKeys.length)
        const nextGroupKey = groupKeys[nextPos]

        const nextEquivalent = groupEntitiesMap[nextGroupKey].find(
          (pair) => pair.rightMenu?.des === param2Value
        )
        const nextPair = nextEquivalent || groupEntitiesMap[nextGroupKey][0]

        return itemsMap[nextPair.pairUuid]
      } else {
        const rightValues = groupEntitiesMap[param1Value]
        const index = rightValues.findIndex(
          (pair) => pair.rightMenu.des === param2Value
        )
        const nextPos = mod(index + direction, rightValues.length)
        return itemsMap[rightValues[nextPos].pairUuid]
      }
    },
    [
      carouselItems,
      param1Value,
      param2Value,
      groupKeys,
      groupEntitiesMap,
      itemsMap
    ]
  )

  if (!current) return null

  const onClickNavigationSlider = (command: NavigationSliderCommand) => {
    const direction = command.includes('next') ? 1 : -1
    const isMain = command.includes('main')
    const next = getNextItem(direction, isMain)
    next && updateUrl(next)
  }

  const labels: [string, string?] = [
    current.navigationMenu[0]?.des,
    current.navigationMenu[1]?.des
  ]

  return (
    <StyledContainer $isLoading={false} style={styleProps}>
      <DynamicInfoCardHighlight
        navigationHeading={''}
        navigationSliderLabels={labels}
        des={<SafeHtmlContent html={current.des} />}
        complement={
          current.complement && <SafeHtmlContent html={current.complement} />
        }
        onClickNavigationSlider={onClickNavigationSlider}
        image={current.image}
        bulletPoints={current.bulletPoints}
        text={current.text && <SafeHtmlContent html={current.text} />}
        showNavigationSliders={showNavigationSliders}
        callToAction={{
          label: current.button?.label,
          href: current.button?.href,
          style: current.button?.style,
          disabled: current.button?.disabled
        }}
        mainNavigationColor={current.navigationMenu[0]?.cor_representativa}
        secondaryNavigationColor={current.navigationMenu[1]?.cor_representativa}
        isLoading={false}
        buttonPosition={current.buttonPosition}
        complementPosition={current.complementPosition}
        secondaryNavigationDisabled={
          groupEntitiesMap[param1Value || '']?.length <= 1
        }
        imagePosition={current.imagePosition}
        imageFormat={current.imageFormat}
        bulletPointIconColor={current.bulletPointIconColor}
        trackingObjectclassUuid={trackingObjectclassUuid}
      />
    </StyledContainer>
  )
}

export default DynamicInfoCardHighlightContainer
